// B607: rendering the cell shapes the TYPED wire (`TypedRows` 0x1A) produces.
//
// Before B607 the server sent every cell as JSON text, so the whole cell domain
// was `null | boolean | number | string | object`, and `String(v)` /
// `JSON.stringify(v)` covered it. The typed decoder in `kinedb.js` returns four
// more JS shapes, and the two old calls get every one of them WRONG:
//
//   `Uint8Array`      (a BYTES cell)      `String(v)`      → "222,173,190,239"
//   `BigInt`          (an INT past 2^53)  `JSON.stringify` → THROWS TypeError
//   `Float32Array`    (a VECTOR cell)     `JSON.stringify` → {"0":1,"1":2}
//   `Uint8Array[]`    (a BLOB's hashes)   `JSON.stringify` → the same soup
//
// This module is deliberately free of React and of the transport: both the
// query console and the report table viz format cells, and both need the same
// answer. Keeping it standalone also keeps it testable without a DOM.

/** Bytes of a hex-rendered cell before the ellipsis. A BYTES column can hold a
 *  megabyte; one such row must not lock the browser up laying out hex digits. */
const DEFAULT_HEX_CAP = 64;

/** Elements of a vector shown before the ellipsis. Embeddings are 768+ wide. */
const DEFAULT_VECTOR_CAP = 8;

/**
 * Render bytes as the `\xdeadbeef` literal kinedb's CLI and SQL both use.
 * Truncates at `maxBytes` and marks the truncation, so the string a table cell
 * gets is bounded no matter how large the column is.
 */
export function bytesToHex(bytes, maxBytes = DEFAULT_HEX_CAP) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || 0);
  const n = Math.min(u8.length, maxBytes);
  let s = '\\x';
  for (let i = 0; i < n; i++) s += u8[i].toString(16).padStart(2, '0');
  return u8.length > maxBytes ? s + '…' : s;
}

/** Render a VECTOR cell. Caps the element count and states the real dimension. */
export function vectorToText(vec, maxItems = DEFAULT_VECTOR_CAP) {
  const n = Math.min(vec.length, maxItems);
  const head = Array.from(vec.subarray(0, n)).join(', ');
  return vec.length > maxItems ? `[${head}, …] (${vec.length})` : `[${head}]`;
}

/** True when `v` is a BLOB cell: the decoder returns the 32-byte chunk hashes. */
function isBlobCell(v) {
  return Array.isArray(v) && v.length > 0 && v.every(e => e instanceof Uint8Array);
}

/**
 * Format one of the FOUR typed shapes as display text.
 *
 * Returns `null` for anything else — that is the signal for the caller to keep
 * its existing rendering. Every UI site that formats cells keeps its old code
 * path intact and only consults this first, so a plain string, number, boolean
 * or JSON object renders exactly as it did before B607.
 */
export function formatTypedCell(v, { maxBytes = DEFAULT_HEX_CAP, maxItems = DEFAULT_VECTOR_CAP } = {}) {
  if (typeof v === 'bigint') return v.toString();
  if (v instanceof Uint8Array) return bytesToHex(v, maxBytes);
  if (v instanceof Float32Array || v instanceof Float64Array) return vectorToText(v, maxItems);
  // A BLOB is a list of content hashes, not the content. Printing the hashes
  // says nothing a reader can use, so we print the shape — the same text the
  // CLI prints, and the same text the untyped `value_to_json` path produced.
  if (isBlobCell(v)) return `<blob, ${v.length} chunk${v.length === 1 ? '' : 's'}>`;
  return null;
}

/**
 * A `JSON.stringify` replacer that survives the typed shapes.
 *
 * `JSON.stringify` THROWS on a BigInt, and the query console stringifies result
 * rows in four places (the JSON tab, the file export, the live-diff comparison
 * and the watch-feed preview). Without this, one `INT` past 2^53 would take the
 * whole page down instead of rendering a long number.
 */
export function jsonSafeReplacer(_key, v) {
  if (typeof v === 'bigint') return v.toString();
  if (v instanceof Uint8Array) return bytesToHex(v);
  if (v instanceof Float32Array || v instanceof Float64Array) return Array.from(v);
  return v;
}
